import './Page.css';

const packages = [
  {
    name: 'SUPPORT SESSION',
    price: '$75',
    period: '/ 1 Hour',
    note: 'Valid for 1 month from purchase.',
    desc: 'Targeted help for students who need a focused, one-off session — whether you\'re stuck on an essay or need a quick strategy check.',
    uses: ['College essay feedback', 'UC PIQs review', 'Common App walkthrough', 'Resume / activities polishing', 'College list strategy', 'Scholarship essay feedback', 'Admissions Q&A'],
  },
  {
    name: 'APPLICATION PACKAGE',
    price: '$350',
    period: '/ 5 Hours',
    note: 'Valid for 3 months from purchase.',
    desc: 'Dive deep — map out a full application strategy, refine multiple essays, and perfect your college list. Great for multi-school applicants.',
    uses: ['Common App & supplemental essay coaching', 'UC PIQs review and selection', 'College list strategy & planning', 'Resume & activities development', 'Interview prep', 'Scholarship applications'],
    featured: true,
  },
  {
    name: 'MASTERY PACKAGE',
    price: '$825',
    period: '/ 12 Hours',
    note: 'Valid for 1 month from purchase.',
    desc: 'End-to-end support through every step of the college journey — from strategy through final submissions.',
    uses: ['Kick-off strategy session & timeline mapping', 'Personal statement coaching (brainstorm → final)', 'Common App supplemental essay development', 'UC Personal Insight Question crafting', 'Scholarship research & application guidance', 'Interview prep & mock Q&A', 'Submission checklists & deadline management'],
  },
];

const whyBoxes = [
  { icon: '✍️', title: 'Essay Coaching', desc: 'Brainstorm, outline, and revise your Common App personal statement and school-specific supplements with expert feedback.' },
  { icon: '🗺️', title: 'College List Strategy', desc: 'Build a balanced reach/match/safety list and align Early Action, Early Decision, and Regular Decision timelines.' },
  { icon: '📋', title: 'Resume & Activities', desc: 'Write concise, impact-driven Common App activity entries and a polished high school resume.' },
  { icon: '🎤', title: 'Interview Prep', desc: 'Practice with mock interviews, get feedback on tone and delivery, and walk in ready to impress.' },
  { icon: '💰', title: 'Scholarship Guidance', desc: 'Identify merit-based opportunities and craft compelling scholarship essay responses.' },
  { icon: '📅', title: 'Early Planning', desc: 'For grades 9–11: align coursework, extracurriculars, and summer plans with long-term admissions goals.' },
];

export default function CollegePage() {
  return (
    <div className="page-wrap">

      {/* Hero */}
      <div className="sat-hero">
        <div className="container">
          <div className="exam-hero-badge">College Counseling</div>
          <h1>Your strongest application<br />starts here.</h1>
          <p className="sat-hero-sub">
            1:1 expert guidance for college essays, school selection, application
            timelines, interviews, and scholarships — freshman year through senior deadlines.
          </p>
          <p className="sat-hero-proof">★★★★★ &nbsp; Grades 9–12 · Live 1:1 via Zoom · Nationwide</p>
          <div className="sat-hero-btns">
            <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-gold">Book a Free Consultation</a>
            <a href="#packages" className="btn-outline-white">View Packages →</a>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="exam-stats-strip">
        <div className="container">
          <div className="exam-stats-inner">
            <div>
              <div className="exam-stat-num">3</div>
              <div className="exam-stat-label">Flexible Packages</div>
            </div>
            <div>
              <div className="exam-stat-num">From $75</div>
              <div className="exam-stat-label">Pay as You Go</div>
            </div>
            <div>
              <div className="exam-stat-num">9–12</div>
              <div className="exam-stat-label">Grade Levels</div>
            </div>
            <div>
              <div className="exam-stat-num">1:1</div>
              <div className="exam-stat-label">Live via Zoom</div>
            </div>
          </div>
        </div>
      </div>

      <div className="container page-body">

        {/* What we cover */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 32 }}>
            <h2 className="section-heading" style={{ marginBottom: 8 }}>Everything the application requires.</h2>
            <p className="section-sub">All sessions are 1:1, live via Zoom, and fully tailored to your goals.</p>
          </div>
          <div className="why-boxes">
            {whyBoxes.map(w => (
              <div className="why-box" key={w.title}>
                <div className="why-box-icon">{w.icon}</div>
                <div className="why-box-title">{w.title}</div>
                <div className="why-box-desc">{w.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Packages */}
        <div className="page-section" id="packages">
          <div className="text-center" style={{ marginBottom: 32 }}>
            <h2 className="section-heading" style={{ marginBottom: 8 }}>Pricing & Packages</h2>
            <p className="section-sub">Choose the level of support that fits your student's timeline and goals.</p>
          </div>
          <div className="pricing-cards">
            {packages.map(p => (
              <div className={`pricing-card${p.featured ? ' featured' : ''}`} key={p.name}>
                <div className="pricing-plan-name">{p.name}</div>
                <div className="pricing-price">{p.price}</div>
                <div className="pricing-period">{p.period}</div>
                <p style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 8 }}>{p.note}</p>
                <p className="pricing-desc">{p.desc}</p>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-gray)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Use this for:</p>
                <ul className="pricing-features">
                  {p.uses.map(u => <li key={u}>{u}</li>)}
                </ul>
                <a href="/contact" className="btn-gold" style={{ display: 'block', textAlign: 'center', marginTop: 'auto' }}>Get Started</a>
              </div>
            ))}
          </div>
          <p className="page-note">📌 Hours reflect counselor-led sessions only. Most students complete 8–15 hours of independent work between meetings.</p>
        </div>

        {/* CTA */}
        <div className="cta-strip" style={{marginBottom: 32}}>
          <h3>Ready to Build Your Best Application?</h3>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15, marginBottom: 24 }}>
            Book a free consultation and we'll map out the right plan for your student's timeline.
          </p>
          <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-gold">Book a Consultation</a>
        </div>

      </div>
    </div>
  );
}
